import React, { useMemo } from "react";

// Lightweight, dependency-free markdown → HTML for AI answers.
// Content comes from our own LLM; we still escape all HTML first, then
// only re-introduce a known-safe set of tags.

function escapeHtml(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function inline(s) {
  let out = escapeHtml(s);
  // links [text](url)
  out = out.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    '<a href="$2" target="_blank" rel="noreferrer">$1</a>'
  );
  // bold **text** or __text__
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  // italic *text* / _text_
  out = out.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  out = out.replace(/(^|[^_])_([^_\n]+)_/g, "$1<em>$2</em>");
  // inline code `code`
  out = out.replace(/`([^`]+)`/g, "<code>$1</code>");
  // inline source citations [1] / [2][3] → styled chips
  out = out.replace(/\[(\d{1,2})\]/g, '<sup class="md-cite">$1</sup>');
  return out;
}

function toHtml(md) {
  if (!md) return "";
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const html = [];
  let list = null; // "ul" | "ol" | null
  let para = [];

  const flushPara = () => {
    if (para.length) {
      html.push(`<p>${inline(para.join(" "))}</p>`);
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      html.push(`</${list}>`);
      list = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const trimmed = line.trim();

    if (!trimmed) {
      flushPara();
      flushList();
      continue;
    }

    const heading = trimmed.match(/^(#{1,4})\s+(.*)$/);
    const ulItem = trimmed.match(/^[-*]\s+(.*)$/);
    const olItem = trimmed.match(/^\d+[.)]\s+(.*)$/);
    const quote = trimmed.match(/^>\s+(.*)$/);
    const hr = /^(-{3,}|\*{3,}|_{3,})$/.test(trimmed);

    if (heading) {
      flushPara();
      flushList();
      const level = heading[1].length + 1; // h2..h5
      html.push(`<h${level}>${inline(heading[2])}</h${level}>`);
    } else if (hr) {
      flushPara();
      flushList();
      html.push("<hr>");
    } else if (ulItem) {
      flushPara();
      if (list !== "ul") {
        flushList();
        list = "ul";
        html.push("<ul>");
      }
      html.push(`<li>${inline(ulItem[1])}</li>`);
    } else if (olItem) {
      flushPara();
      if (list !== "ol") {
        flushList();
        list = "ol";
        html.push("<ol>");
      }
      html.push(`<li>${inline(olItem[1])}</li>`);
    } else if (quote) {
      flushPara();
      flushList();
      html.push(`<blockquote>${inline(quote[1])}</blockquote>`);
    } else {
      flushList();
      para.push(trimmed);
    }
  }
  flushPara();
  flushList();
  return html.join("\n");
}

export default function Markdown({ text, className = "" }) {
  const html = useMemo(() => toHtml(text || ""), [text]);
  return (
    <div
      className={`answer-md ${className}`.trim()}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
